import { getAuth } from 'firebase/auth';
import { collection, query, where, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { geohashQueryBounds, distanceBetween } from 'geofire-common';

/**
 * Returns all time-capsules within `radius` meters of `center` (lat, lng).
 */
export async function fetchNearbyTimeCapsules({ center, radius = 1000 }) {
    const auth = getAuth();
    const userId = auth.currentUser?.uid;
    const now = new Date();

    // Compute geohash query bounds
    const bounds = geohashQueryBounds([center.lat, center.lng], radius);

    // Fire off both public & (if logged in) private queries
    const snaps = await Promise.all(
        bounds.flatMap(([start, end]) => {
            const pubQ = query(
                collection(db, 'timeCapsules'),
                where('geohash', '>=', start),
                where('geohash', '<=', end),
                where('status', '==', 'published'),
                where('capsuleType', '!=', 'private'),
            );
            const arr = [getDocs(pubQ)];

            if (userId) {
                const privQ = query(
                    collection(db, 'timeCapsules'),
                    where('geohash', '>=', start),
                    where('geohash', '<=', end),
                    where('status', '==', 'delivered'),
                    where('capsuleType', '==', 'private'),
                    where('recipientUserIds', 'array-contains', userId),
                );
                arr.push(getDocs(privQ));
            }

            return arr;
        })
    );

    // Flatten & filter by distance, reveal/end dates
    const allCaps = [];
    snaps.flat().forEach((snap) => {
        snap.docs.forEach((d) => {
            const data = d.data();
            const endDate = data.endDate?.toDate();
            if (endDate && endDate < now) return;

            const dist = distanceBetween(
                [center.lat, center.lng],
                [data.location.lat, data.location.lng]
            );

            const reveal = data.revealDate?.toDate();
            // allow private always; for others require revealDate <= now or no revealDate
            const isVisible =
                data.capsuleType === 'private' ||
                !reveal ||
                reveal <= now;

            if (dist <= radius && isVisible) {
                allCaps.push({ id: d.id, ...data });
            }
        });
    });

    return allCaps;
}

/**
 * Fetch a single time-capsule by ID.
 */
export async function fetchTimeCapsuleById(id) {
    const snap = await getDoc(doc(db, 'timeCapsules', id));
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}
